import { useEffect, useState, useCallback, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { fbt } from 'fbtee'
import { pageTitle, useDocumentTitle } from '../hooks/useDocumentTitle'
import { cancelButtonLabel, backToHomeButtonLabel, joinGroupFailedToast } from '../lib/commonStrings'
import { useAuth } from '../hooks/useAuth'
import { useGuestLandingSession } from '../hooks/useGuestLandingSession'
import { recordTrace, useTraceStart } from '../lib/perf'
import { joinGroup, resolveInviteCode, setPendingGroupJoin } from '../lib/groupJoin'
import { isFestivalInvite, festivalInviteEventId } from '../lib/inviteEntryPoint'
import { classifyError } from '../lib/errors'
import { log } from '../lib/logger'
import { toastError } from '../lib/toast'
import { track } from '../lib/analytics'
import { useTransitionNavigate } from '../lib/viewTransition'
import { Button } from '../components/ui/button'
import type { InviteCodePreview } from '@shared/groupJoin'
import { LinkLandingMessage, LinkLandingShell, LinkLandingSpinner } from '../components/LinkLandingState'

type Status = 'auth' | 'resolving' | 'confirm' | 'joining' | 'invalid'

export default function JoinPage() {
  useDocumentTitle(pageTitle(fbt('Join group', 'Browser tab title for the invite join page')))
  const { code } = useParams<{ code: string }>()
  const location = useLocation()
  // Pinned at mount: the marker rides the URL in, and the join can happen
  // several navigations later.
  const [fromFestival] = useState(() => isFestivalInvite(location.search))
  // A festival invite carries the event, so joining drops the person straight
  // into that festival rather than the group page away from it.
  const [festivalEventId] = useState(() => festivalInviteEventId(location.search))
  const groupDest = useCallback(
    (groupId: string) =>
      festivalEventId ? `/live-festival/${festivalEventId}` : `/groups/${groupId}`,
    [festivalEventId],
  )
  const entryPoint = fromFestival ? 'festival_qr' : 'invite_link'
  const { user } = useAuth()
  const navigate = useTransitionNavigate()
  const [status, setStatus] = useState<Status>('auth')
  const [group, setGroup] = useState<InviteCodePreview | null>(null)
  const mountedAt = useTraceStart()
  const traceRecorded = useRef(false)

  // Switch to 'resolving' synchronously during render once we have a user + code,
  // so the effect body doesn't need to call setState.
  const lookupKey = user && code ? `${user.uid}|${code}` : null
  const [resolvedKey, setResolvedKey] = useState<string | null>(null)
  if (lookupKey && lookupKey !== resolvedKey) {
    setResolvedKey(lookupKey)
    setStatus('resolving')
  }

  useGuestLandingSession(fromFestival ? 'qr' : 'invite_link')

  // Once we have a user, look up the group
  useEffect(() => {
    if (!user || !code) return
    let cancelled = false

    async function run() {
      const inviteCode = code!.toUpperCase()
      try {
        const found = await resolveInviteCode(inviteCode)
        if (cancelled) return
        if (!traceRecorded.current) {
          traceRecorded.current = true
          recordTrace(
            'join_resolve',
            mountedAt.wall,
            performance.now() - mountedAt.perf,
            { found: String(!!found) },
          )
        }
        if (!found) {
          setStatus('invalid')
          return
        }
        if (found.isMember) {
          navigate(groupDest(found.id), { replace: true, state: { entryPoint: 'invite' } })
          return
        }
        if (user!.isAnonymous) {
          // Sign-in usually links the anonymous session and keeps the UID, but a
          // returning user's account can't be linked — stash the groupId so
          // GroupPage can add the new UID in that case.
          setPendingGroupJoin({ groupId: found.id, code: inviteCode })
          try {
            const { alreadyMember } = await joinGroup({ groupId: found.id, code: inviteCode })
            if (!alreadyMember) track('group_guest_joined', { group_id: found.id, entry_point: entryPoint })
          } catch (err) {
            log('join', 'guest join failed', { groupId: found.id, error: String(err) })
            // Still navigate — group reads are allowed for any authenticated
            // user, and the sign-in upgrade re-tries with the stashed code.
          }
          navigate(`/groups/${found.id}`, { replace: true, state: { entryPoint: 'invite' } })
          return
        }
        setGroup(found)
        setStatus('confirm')
      } catch (err) {
        if (cancelled) return
        toastError(
          fbt("Couldn't load group", 'Toast error when resolving an invite link fails'),
          classifyError(err),
        )
        navigate('/', { replace: true })
      }
    }

    run()
    return () => { cancelled = true }
  }, [user, code, navigate, entryPoint, mountedAt, groupDest])

  const handleJoin = useCallback(async () => {
    if (!group || !user) return
    setStatus('joining')
    try {
      await joinGroup({ groupId: group.id, code: code!.toUpperCase() })
      track('group_joined', { group_id: group.id, entry_point: entryPoint })
      track('social_connected', { entry_point: 'group' })
      toast.success(
        fbt('Joined ' + fbt.param('groupName', group.name), 'Toast after successfully joining a group'),
      )
      navigate(groupDest(group.id), { replace: true, state: { entryPoint: 'invite' } })
    } catch (err) {
      toastError(
        joinGroupFailedToast(),
        classifyError(err),
      )
      navigate('/', { replace: true })
    }
  }, [group, user, code, navigate, entryPoint, groupDest])

  if (status === 'invalid') {
    return (
      <LinkLandingMessage
        title={
          <fbt desc="Heading when an invite link is not valid">
            This invite link isn't valid
          </fbt>
        }
        body={
          <fbt desc="Body text suggesting what to do when an invite link is invalid">
            Double-check the link, or ask the group for a fresh one.
          </fbt>
        }
        action={
          <Button variant="outline" size="sm" onClick={() => navigate('/', { replace: true })}>
            {backToHomeButtonLabel()}
          </Button>
        }
      />
    )
  }

  if (status === 'confirm' && group) {
    return (
      <LinkLandingShell className="gap-6">
        <div className="space-y-2">
          <h1 className="text-xl font-semibold tracking-tight">
            <fbt desc="Heading asking the user to confirm joining a group">
              Join group?
            </fbt>
          </h1>
          <p className="text-muted-foreground">
            <fbt desc="Body text showing the group name the user is about to join">
              You've been invited to join <fbt:param name="groupName">{group.name}</fbt:param>.
            </fbt>
          </p>
        </div>
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={() => navigate('/', { replace: true })} className="cursor-pointer">
            {cancelButtonLabel()}
          </Button>
          <Button onClick={handleJoin} className="cursor-pointer">
            <fbt desc="Button to confirm joining a group">Join group</fbt>
          </Button>
        </div>
      </LinkLandingShell>
    )
  }

  const spinnerLabel =
    status === 'joining' && group
      ? fbt(
          'Joining ' + fbt.param('groupName', group.name) + '…',
          'Loading message while joining a group by invite link, shows group name',
        )
      : fbt('Looking up invite…', 'Loading message while resolving an invite link')

  return <LinkLandingSpinner label={spinnerLabel} />
}
